// Browser client for the Stack backend. Direct fetch — the Stack API has
// CORS configured to accept requests from the SWA / localhost:5173 origins.
//
// Auth: `acquireStackToken()` (see src/lib/auth/msal.ts) is called per
// request. It currently returns the Lens API token because Stack's backend
// verifies JWT signature only, not audience, and both tokens are Entra-signed
// under the same tenant. When Stack gets a dedicated Entra app registration
// and its backend adds audience validation, only that helper needs updating —
// call sites do not.
import { acquireStackToken } from '../auth/msal'

const BASE_URL = (import.meta.env.VITE_STACK_API_URL as string | undefined) ?? 'https://func-lens-stack-d2hhcahbh9fygvdb.northeurope-01.azurewebsites.net/api'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${path}`
  const method = (init?.method ?? 'GET').toUpperCase()
  const token = await acquireStackToken()

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  }
  if (init?.body !== undefined && init.body !== null) {
    headers['Content-Type'] = 'application/json'
  }

  let res: Response
  try {
    res = await fetch(url, { method, headers, body: init?.body as BodyInit | null | undefined })
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    console.error(`[Stack] fetch failed — ${method} ${url}:`, err)
    throw new Error(`Cannot reach Stack API at ${url}: ${msg}`)
  }

  const text = await res.text()
  if (!res.ok) {
    let errorMsg = `HTTP ${res.status}`
    try {
      const parsed = JSON.parse(text) as { error?: string }
      if (parsed.error) errorMsg = `${res.status} ${parsed.error}`
    } catch { /* use default */ }
    console.error(`[Stack] ${res.status} ${method} ${url}:`, text)
    throw new Error(errorMsg)
  }

  if (!text) return undefined as T
  return JSON.parse(text) as T
}

export type NodeType = 'drawer' | 'divider' | 'group' | 'item'
export type Visibility = 'org' | 'restricted' | 'personal'

export interface StackNode {
  id: string
  nodeType: NodeType
  parentId: string | null
  orgId: string
  createdBy: string
  createdAt: string
  modifiedAt: string
  deletedAt?: string | null
  name: string
  icon?: string
  body?: string
  visibility: Visibility
  allowedPrincipals: string[]
  // Drawer
  drawerId?: string
  parentDrawerId?: string | null
  ancestorDrawerIds?: string[]
  color?: string
  // Divider
  dividerId?: string
  // Group
  groupId?: string
  // Item
  itemId?: string
  itemType?: string
  blobRef?: string
  summary?: string
  tags?: string[]
  aiProcessed?: boolean
  fileSizeBytes?: number
  originalFilename?: string
}

export interface CreateNodePayload {
  nodeType: NodeType
  parentId: string | null
  name: string
  icon?: string
  body?: string
  color?: string
  visibility?: Visibility
}

export interface UploadSession {
  sessionId: string
  sasUrl: string
}

export interface AIMetadata {
  summary: string
  tags: string[]
  suggestedGroup: string
  confidence: 'high' | 'medium' | 'low'
  processable: boolean
}

export interface ItemVersion {
  versionId: string
  blobRef: string
  originalFilename: string
  fileSizeBytes: number
  uploadedAt: string
  uploadedBy: string
  summary?: string
  tags?: string[]
}

export interface ShareInfo {
  visibility: Visibility
  allowedPrincipals: string[]
}

export interface AuditEvent {
  id: string
  orgId: string
  actorOid: string
  action: string
  nodeType: string
  nodeId: string
  nodeName: string
  timestamp: string
  meta?: Record<string, unknown>
}

export const stackApi = {
  getChildren: (parentId: string | null, nodeType?: NodeType) => {
    const params = new URLSearchParams()
    if (parentId) params.set('parentId', parentId)
    if (nodeType) params.set('nodeType', nodeType)
    return request<StackNode[]>(`/nodes?${params}`)
  },

  getNode: (id: string) =>
    request<StackNode>(`/nodes/${id}`),

  createNode: (payload: CreateNodePayload) =>
    request<StackNode>('/nodes', { method: 'POST', body: JSON.stringify(payload) }),

  updateNode: (id: string, fields: Partial<Pick<StackNode, 'name' | 'icon' | 'body' | 'visibility' | 'color'>>) =>
    request<StackNode>(`/nodes/${id}`, { method: 'PATCH', body: JSON.stringify(fields) }),

  deleteNode: (id: string) =>
    request<void>(`/nodes/${id}`, { method: 'DELETE' }),

  restoreNode: (id: string) =>
    request<void>(`/nodes/${id}/restore`, { method: 'POST' }),

  search: (q: string, scope?: string) => {
    const params = new URLSearchParams({ q })
    if (scope) params.set('scope', scope)
    return request<StackNode[]>(`/search?${params}`)
  },

  createUploadSession: (payload: { drawerId: string; dividerId: string; groupId: string; filename: string; mimeType: string }) =>
    request<UploadSession>('/upload/session', { method: 'POST', body: JSON.stringify(payload) }),

  processUpload: (sessionId: string) =>
    request<AIMetadata>('/upload/process', { method: 'POST', body: JSON.stringify({ sessionId }) }),

  confirmUpload: (sessionId: string, overrides?: Partial<AIMetadata>) =>
    request<StackNode>('/upload/confirm', { method: 'POST', body: JSON.stringify({ sessionId, ...overrides }) }),

  abandonUpload: (sessionId: string) =>
    request<void>(`/upload/session/${sessionId}`, { method: 'DELETE' }),

  getVersions: (itemId: string) =>
    request<ItemVersion[]>(`/nodes/${itemId}/versions`),

  restoreVersion: (itemId: string, versionId: string) =>
    request<void>(`/nodes/${itemId}/versions/restore`, { method: 'POST', body: JSON.stringify({ versionId }) }),

  getShare: (nodeId: string) =>
    request<ShareInfo>(`/nodes/${nodeId}/share`),

  setShare: (nodeId: string, info: ShareInfo) =>
    request<void>(`/nodes/${nodeId}/share`, { method: 'POST', body: JSON.stringify(info) }),

  getAuditLog: (params?: { from?: string; to?: string; action?: string }) => {
    const p = new URLSearchParams()
    if (params?.from) p.set('from', params.from)
    if (params?.to) p.set('to', params.to)
    if (params?.action) p.set('action', params.action)
    return request<AuditEvent[]>(`/audit?${p}`)
  },
}
